import React, { useState, useEffect, useContext } from 'react';
import { useVsCodeDashboard, USER_TYPES } from '../applicationLayer/dashBoardHooks/vsCodeDashboardHook';
import { useTeamDashboard } from '../applicationLayer/dashBoardHooks/teamDashboardHook';
import { VsCodeAuthContext } from '../contextApis/vsCodeAuthContext';
import Sidebar from '../components/dashBoardComponents/vsCodeDashboard/Sidebar';
import Overview from '../components/dashBoardComponents/vsCodeDashboard/Overview';
import Settings from '../components/dashBoardComponents/vsCodeDashboard/Settings';
import ContactSupport from '../components/dashBoardComponents/vsCodeDashboard/ContactSupport';
import Billing from '../components/dashBoardComponents/vsCodeDashboard/Billing';
import TeamOverview from '../components/dashBoardComponents/vsCodeDashboard/TeamOverview';
import TeamSettings from '../components/dashBoardComponents/vsCodeDashboard/TeamSettings';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';

const VsCodeDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [teamInitialized, setTeamInitialized] = useState(false);

  // Get logout function from auth context
  const { logout } = useContext(VsCodeAuthContext);

  const {
    userInfo,
    userType,
    teamId,
    subscriptions,
    usageHistory,
    paymentInfo,
    planType,
    loading,
    error,
    fetchPeriodUsage,
    fetchBillingHistory,
    fetchInvoices,
    billingHistory,
    invoices
  } = useVsCodeDashboard();

  // Team dashboard hook
  const teamDashboard = useTeamDashboard();

  const isTeamAdmin = userType === USER_TYPES.TEAM_ADMIN;
  const isTeamMember = userType === USER_TYPES.TEAM_MEMBER;
  const isTeamUser = isTeamAdmin || isTeamMember;

  // Initialize team dashboard for team users
  useEffect(() => {
    if (isTeamUser && teamId && !teamInitialized && !loading) {
      teamDashboard.initializeTeamDashboard(teamId);
      setTeamInitialized(true);
      // Set default tab to team-overview for team users
      setActiveTab('team-overview');
    }
  }, [isTeamUser, teamId, teamInitialized, loading, teamDashboard]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const renderContent = () => {
    switch (activeTab) {
      // Team tabs
      case 'team-overview':
        return isTeamUser ? (
          <TeamOverview
            teamInfo={teamDashboard.teamInfo}
            members={teamDashboard.members}
            teamUsage={teamDashboard.teamUsage}
            membersUsage={teamDashboard.membersUsage}
            usageHistory={teamDashboard.usageHistory}
            fetchUsageHistory={teamDashboard.fetchUsageHistory}
            onTabChange={handleTabChange}
          />
        ) : (
          <Overview
            userInfo={userInfo}
            subscriptions={subscriptions}
            usageHistory={usageHistory}
            fetchPeriodUsage={fetchPeriodUsage}
            onTabChange={handleTabChange}
            paymentInfo={paymentInfo}
          />
        );
      case 'team-settings':
        return isTeamAdmin ? (
          <TeamSettings
            teamInfo={teamDashboard.teamInfo}
            members={teamDashboard.members}
            activityLog={teamDashboard.activityLog}
            activityHasMore={teamDashboard.activityHasMore}
            onRemoveMember={teamDashboard.removeTeamMember}
            onUpdateTeam={teamDashboard.updateTeamDetails}
            onRegenerateInvite={teamDashboard.regenerateInvite}
            onSendInvites={teamDashboard.sendTeamInvites}
            loadMoreActivities={teamDashboard.loadMoreActivities}
            loading={teamDashboard.loading}
          />
        ) : (
          <Settings userInfo={userInfo} onTabChange={handleTabChange} subscriptions={subscriptions} paymentInfo={paymentInfo} />
        );

      // Personal tabs
      case 'overview':
        return (
          <Overview
            userInfo={userInfo}
            subscriptions={subscriptions}
            usageHistory={usageHistory}
            fetchPeriodUsage={fetchPeriodUsage}
            onTabChange={handleTabChange}
            paymentInfo={paymentInfo}
          />
        );
      case 'billing':
        return <Billing userInfo={userInfo} planType={planType} subscriptions={subscriptions} />;
      case 'settings':
        return <Settings userInfo={userInfo} onTabChange={handleTabChange} subscriptions={subscriptions} paymentInfo={paymentInfo} />;
      case 'contact':
        return <ContactSupport userInfo={userInfo} />;
      default:
        return (
          <Overview
            userInfo={userInfo}
            subscriptions={subscriptions}
            usageHistory={usageHistory}
            fetchPeriodUsage={fetchPeriodUsage}
            onTabChange={handleTabChange}
            paymentInfo={paymentInfo}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#0a0a0f] text-white">
        <LoadingSpinner />
      </div>
    );
  }

  const handleBackToLogin = () => {
    // Clear any cached auth data
    localStorage.removeItem('web_access_token');
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('is_authenticated');
    localStorage.removeItem('email');

    // Redirect to signin page
    window.location.href = '/vscode/signin';
  };

  if (error) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-[#0a0a0f] p-8">
        <Card className="w-full max-w-lg border-red-500/20 bg-[#121219]/90 backdrop-blur-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/20">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <CardTitle className="text-2xl text-red-500">Dashboard Error</CardTitle>
            <CardDescription>
              We encountered an issue while loading your dashboard. Please try signing in again.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-4 font-mono text-xs text-red-400 break-words">
              {error}
            </div>
            <div className="flex justify-center">
              <Button onClick={handleBackToLogin} variant="outline" className="gap-2">
                <ArrowLeft className="w-4 h-4" /> Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#0a0a0f] overflow-hidden">
      <div className="flex-shrink-0">
        <Sidebar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          userInfo={userInfo}
          userType={userType}
          onLogout={logout}
          planType={planType}
        />
      </div>
      <main className="flex-1 overflow-auto pl-[280px]">
        {renderContent()}
      </main>
    </div>
  );
};

export default VsCodeDashboard;
